import { loadAssetManifest, assetManifestNow, type AssetManifest } from "./assetManifest";
import { bundledArtPath, clampLoopFps } from "@greg-chick/shared";
import { safeImageUrl } from "./safeUrl";

// 메인 화면 교체 가능 에셋(배경·캐릭터) + 위치·배율. 관리자 업로드(매니페스트) 우선 → /art SVG 폴백.
export interface LandingPiece {
  url: string;
  pos?: { x: number; y: number }; // 무대 내 상대 위치(0~1, 중심 기준)
  scale?: number;
  z?: number; // 앞뒤 순서(CSS zIndex). 미설정=기본 배치순서
  fps?: number; // 스트립 재생 속도(0.2~3 클램프 — 시각안전 3Hz 미만)
}
/** 메인 무대에 서는 캐릭터 1명 — 정지 이미지(url) + 선택적 애니 스트립(strip). */
export interface LandingChar {
  id: string; // landing.char.<id> 의 id (greg=왼쪽 기본, chick=오른쪽 기본, 그 외=운영자 추가)
  url: string;
  pos?: { x: number; y: number };
  /** 좁은 화면(폰) 전용 자리 — 운영자가 관리자 「📱 좁은 화면」에서 정한 것. 없으면 기본 규칙. */
  posNarrow?: { x: number; y: number };
  scale?: number;
  z?: number;
  strip?: { url: string; fps?: number }; // landing.char.<id>.strip (있으면 정지 대신 재생)
}
export interface LandingAssets {
  bg?: LandingPiece;
  // 게시된 모든 메인 캐릭터(운영자가 추가한 것 포함). 관리자에서 추가하면 여기에 자동으로 들어온다.
  chars: LandingChar[];
}

// greg/chick 는 기본 2캐릭터 — 게시본이 없으면 번들 SVG 로 폴백(M1 오프라인 동작).
// 번들 기본 그림은 shared 단일 표(관리자 미리보기가 같은 것을 본다 — BUNDLED_ART 주석 참고)
const FALLBACK = (id: string): string | undefined => bundledArtPath(`landing.char.${id}`);
// 렌더 순서(z 동률 시). 알 수 없는 커스텀 id 는 greg 뒤·chick 앞에 온다.
const ORDER: Record<string, number> = { greg: 0, chick: 100 };

const clampZ = (v: unknown): number | undefined =>
  typeof v === "number" && Number.isFinite(v) ? v : undefined;

/** 게시본이 오면 그것으로, 아직이면 번들 정적 아트로 — **기다리지 않고** 첫 화면을 그린다. */
export function landingAssetsNow(): LandingAssets {
  return landingAssetsFrom(assetManifestNow());
}

export async function loadLandingAssets(): Promise<LandingAssets> {
  return landingAssetsFrom(await loadAssetManifest());
}

/**
 * 매니페스트 → 무대에 세울 것들. **순수 함수다** — 받아오는 일과 고르는 일을 나눠 두면
 * 첫 그림을 기다리지 않고 그릴 수 있다(그 기다림이 곧 「가짜 그림이 먼저 보이는」 시간이었다).
 */
export function landingAssetsFrom(m: AssetManifest): LandingAssets {
  const assets = m.assets ?? {};

  const piece = (key: string, fallbackUrl?: string): LandingPiece | undefined => {
    const e = assets[key];
    const url = safeImageUrl(e?.url) ?? fallbackUrl;
    if (!url) return undefined;
    return { url, pos: e?.pos, scale: e?.scale, z: clampZ(e?.z), fps: clampLoopFps(e?.fps) };
  };

  // landing.char.<id> (정지) 와 landing.char.<id>.strip (애니) 을 id 별로 모은다.
  type Raw = { base?: (typeof assets)[string]; strip?: (typeof assets)[string] };
  const byId = new Map<string, Raw>();
  for (const [key, e] of Object.entries(assets)) {
    const mm = /^landing\.char\.(.+?)(\.strip)?$/.exec(key);
    if (!mm) continue;
    const id = mm[1]!;
    const cur = byId.get(id) ?? {};
    if (mm[2]) cur.strip = e;
    else cur.base = e;
    byId.set(id, cur);
  }
  // 기본 2캐릭터는 게시본이 없어도 폴백으로 항상 존재(무대가 비지 않게).
  for (const id of ["greg", "chick"]) if (!byId.get(id)?.base) byId.set(id, { ...(byId.get(id) ?? {}) });

  const chars: LandingChar[] = [];
  for (const [id, raw] of byId) {
    const url = safeImageUrl(raw.base?.url) ?? FALLBACK(id);
    if (!url) continue; // 정지 이미지가 없으면(폴백도 없으면) 렌더 불가 — 건너뜀
    const stripUrl = safeImageUrl(raw.strip?.url);
    chars.push({
      id,
      url,
      pos: raw.base?.pos,
      posNarrow: raw.base?.posNarrow,
      scale: raw.base?.scale,
      z: clampZ(raw.base?.z),
      strip: stripUrl ? { url: stripUrl, fps: clampLoopFps(raw.strip?.fps) } : undefined,
    });
  }
  // z(앞뒤) → 기본 순서(greg 먼저, chick 마지막) 순으로 안정 정렬.
  chars.sort((a, b) => (a.z ?? 0) - (b.z ?? 0) || (ORDER[a.id] ?? 50) - (ORDER[b.id] ?? 50));

  return { bg: piece("landing.hero.default"), chars };
}
